import { useRef, useState, type ReactNode } from 'react';
import {
  addChecklistItem,
  deleteChecklistItem,
  reorderChecklist,
  updateChecklistItem,
  type TaskChecklistItem,
} from '../../lib/tasks';
import { Alert, CHECKBOX_BOX, Input, SkeletonRows } from '../ui';
import { TaskComposer } from './TaskComposer';
import { CheckIcon, ChevronIcon, ChevronUpIcon, GripIcon, TrashIcon } from '../icons';

/**
 * An item that has been typed but not answered for yet.
 *
 * Its own prefix rather than `nextPendingTaskId`'s: these ids never leave this
 * component, and one that `isPendingTask` recognised would be a pending *task*
 * to every other part of the app that asked.
 */
const PENDING_PREFIX = 'pending-item:';
let pendingCount = 0;
const nextPendingId = (): string => `${PENDING_PREFIX}${++pendingCount}`;
const isPendingItem = (id: string): boolean => id.startsWith(PENDING_PREFIX);

/** Where a drop would put the dragged row, relative to the row under the
 *  pointer. */
type DropAt = { id: string; below: boolean };

/**
 * A task's checklist: a title, a tick, and an order you choose.
 *
 * Every mutation here is applied before it is sent and rolled back if it fails.
 * That is the opposite of the panel's field `save()`, which overlays a pending
 * patch and waits, and it is the right call for one reason: a checkbox that
 * waits a round trip to paint does not read as slow, it reads as broken -- so
 * people click it again. A dragged row that snaps back for a moment before
 * settling is the same failure in a different coat.
 *
 * The rollbacks are keyed by item id rather than restoring a snapshot of the
 * array, because a second tick is routinely already in flight when the first one
 * fails, and putting the old array back would untick that one too. Reordering
 * keeps to that rule the only way an order can: it restores the *sequence* of
 * ids and leaves each item's contents as it currently finds them -- see
 * `inOrder`.
 */
export function TaskChecklist({
  taskId,
  items,
  placeholderCount = 0,
  onChange,
}: {
  taskId: string;
  items: TaskChecklistItem[];
  /** How many rows are known to exist but have not been fetched yet, from the
   *  list row's `checklistCount`. Holds the section at its real height while the
   *  detail is in flight, so the sections below do not jump -- the same trick
   *  TaskAttachments uses. */
  placeholderCount?: number;
  /**
   * An updater rather than a value, unlike TaskAttachments' `onChange`.
   *
   * That one closes over a possibly-stale array and gets away with it because
   * nobody uploads two files a second. Ticking happens in bursts and every
   * change here is optimistic, so a rollback runs *after* other changes may have
   * landed -- a value computed from a stale prop would silently undo them.
   */
  onChange: (update: (items: TaskChecklistItem[]) => TaskChecklistItem[]) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  /** The row being dragged, so it can dim while it is in the air. */
  const [draggingId, setDraggingId] = useState<string | null>(null);
  /** Where the drop would land, drawn as a line between two rows. */
  const [dropAt, setDropAt] = useState<DropAt | null>(null);

  const replace = (id: string, patch: Partial<TaskChecklistItem>) =>
    onChange((entries) => entries.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));

  const report = (failure: unknown, fallback: string) =>
    setError(failure instanceof Error ? failure.message : fallback);

  function add(title: string) {
    const pendingId = nextPendingId();
    const now = new Date().toISOString();
    setError(null);
    onChange((entries) => [
      ...entries,
      { id: pendingId, taskId, title, done: false, createdAt: now, updatedAt: now },
    ]);

    // Not awaited: TaskComposer has already cleared and refocused, so the next
    // line is being typed while this one is still in the air.
    void addChecklistItem(taskId, title)
      .then(({ item }) =>
        onChange((entries) => entries.map((entry) => (entry.id === pendingId ? item : entry))),
      )
      .catch((addError: unknown) => {
        report(addError, 'Could not add that item.');
        onChange((entries) => entries.filter((entry) => entry.id !== pendingId));
      });
  }

  async function toggle(item: TaskChecklistItem) {
    const done = !item.done;
    setError(null);
    replace(item.id, { done });
    try {
      await updateChecklistItem(taskId, item.id, { done });
    } catch (toggleError: unknown) {
      report(toggleError, 'Could not save that tick.');
      replace(item.id, { done: item.done });
    }
  }

  async function rename(item: TaskChecklistItem, draft: string) {
    setEditingId(null);
    const title = draft.trim();
    // Nothing to send: blank reverts and unchanged is a no-op, the same rule the
    // panel's EditableTitle follows.
    if (!title || title === item.title) return;

    setError(null);
    replace(item.id, { title });
    try {
      await updateChecklistItem(taskId, item.id, { title });
    } catch (renameError: unknown) {
      report(renameError, 'Could not rename that item.');
      replace(item.id, { title: item.title });
    }
  }

  async function remove(item: TaskChecklistItem) {
    // Captured before the row goes, so a failure puts it back where it was
    // rather than at the bottom.
    const index = items.findIndex((entry) => entry.id === item.id);
    setError(null);
    onChange((entries) => entries.filter((entry) => entry.id !== item.id));
    try {
      await deleteChecklistItem(taskId, item.id);
    } catch (deleteError: unknown) {
      report(deleteError, 'Could not remove that item.');
      onChange((entries) => [...entries.slice(0, index), item, ...entries.slice(index)]);
    }
  }

  /**
   * Put the list in a new order and tell the server.
   *
   * Optimistic like everything else here, and stated as a sequence of ids at
   * every step -- the local write, the reply and the rollback all go through
   * `inOrder`, which rearranges whatever the list currently holds rather than
   * replacing it. A tick that lands mid-drag survives all three.
   *
   * The reply is the whole list, and only its order is taken from it. Taking
   * the items themselves would be the snapshot mistake this component's header
   * warns about, one round trip later.
   */
  function reorder(next: TaskChecklistItem[]) {
    const before = items.map((item) => item.id);
    const after = next.map((item) => item.id);
    // A drag that ends where it started, or an arrow at the end of the list.
    if (before.length === after.length && before.every((id, index) => id === after[index])) return;

    setError(null);
    onChange((entries) => inOrder(entries, after));

    void reorderChecklist(taskId, after)
      .then(({ items: saved }) =>
        onChange((entries) => inOrder(entries, saved.map((item) => item.id))),
      )
      .catch((reorderError: unknown) => {
        report(reorderError, 'Could not save the new order.');
        onChange((entries) => inOrder(entries, before));
      });
  }

  /**
   * Reordering is off while an item is still being created.
   *
   * A pending row has no id the server would recognise, so it cannot be named
   * in an order -- and leaving it out would put it at the end server-side while
   * the panel shows it wherever the drag left it. The two would not disagree
   * for long, only until the next fetch, which is exactly the kind of quiet
   * divergence nobody thinks to look for.
   *
   * The window is one POST wide. It costs a moment of dimmed arrows while a
   * line you just typed is saving, which is the trade every other option here
   * was worse than.
   */
  const settling = items.some((item) => isPendingItem(item.id));
  const doneCount = items.filter((item) => item.done).length;

  return (
    <section>
      <h3 className="flex items-center gap-2 text-dense font-semibold tracking-tight text-stone-700">
        Checklist
        {items.length > 0 ? (
          <span className="font-normal tabular-nums text-stone-500">
            {doneCount}/{items.length}
          </span>
        ) : null}
      </h3>

      {error ? (
        <div className="mt-2">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {items.length > 0 ? (
        <ul
          className="mt-1.5 space-y-0.5"
          // Cleared when the pointer leaves the list rather than on each row's
          // own `dragleave`, which also fires when crossing from one row to the
          // next and would strobe the indicator all the way down.
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropAt(null);
          }}
        >
          {items.map((item, index) => (
            <ChecklistRow
              key={item.id}
              item={item}
              index={index}
              count={items.length}
              editing={editingId === item.id}
              reorderable={!settling && items.length > 1}
              dragging={draggingId === item.id}
              // Never against the row being dragged: a line above the thing in
              // your hand says a drop would move it somewhere it already is.
              dropEdge={
                dropAt && dropAt.id === item.id && draggingId !== item.id
                  ? dropAt.below
                    ? 'below'
                    : 'above'
                  : null
              }
              onToggle={() => void toggle(item)}
              onStartRename={() => setEditingId(item.id)}
              onCancelRename={() => setEditingId(null)}
              onRename={(draft) => void rename(item, draft)}
              onRemove={() => void remove(item)}
              onMove={(delta) => reorder(withMoved(items, item.id, index + delta))}
              onDragStart={() => {
                setDraggingId(item.id);
                setDropAt(null);
              }}
              onDragOver={(below) =>
                setDropAt((current) =>
                  // Only when it has actually changed. `dragover` fires every
                  // few milliseconds, and a fresh object each time would
                  // re-render the whole list for the entire drag.
                  current && current.id === item.id && current.below === below
                    ? current
                    : { id: item.id, below },
                )
              }
              onDrop={(below) => {
                if (draggingId && draggingId !== item.id) {
                  reorder(dropped(items, draggingId, item.id, below));
                }
                setDraggingId(null);
                setDropAt(null);
              }}
              onDragEnd={() => {
                setDraggingId(null);
                setDropAt(null);
              }}
            />
          ))}
        </ul>
      ) : placeholderCount === 0 ? (
        <p className="mt-1.5 text-dense text-stone-500">No checklist yet.</p>
      ) : null}

      <SkeletonRows count={placeholderCount} />

      <div className="mt-1.5">
        <TaskComposer placeholder="Add a checklist item" onSubmit={add} />
      </div>
    </section>
  );
}

/**
 * The same items, in the order these ids give.
 *
 * The rearrangement every path here goes through, and the reason none of them
 * has to hold a snapshot of the array. Items the ids do not mention keep their
 * relative order at the end -- which is a line typed during the round trip,
 * landing where the composer put it.
 */
function inOrder(entries: TaskChecklistItem[], ids: string[]): TaskChecklistItem[] {
  const rank = new Map(ids.map((id, index) => [id, index]));
  const known = entries
    .filter((entry) => rank.has(entry.id))
    .sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));

  return [...known, ...entries.filter((entry) => !rank.has(entry.id))];
}

/** One item lifted out and put back at `to`, which is an index into the list
 *  once it has been lifted out -- the frame both callers below already think
 *  in. Out-of-range clamps rather than throwing, so an arrow at either end of
 *  the list is a no-op instead of a crash. */
function withMoved(entries: TaskChecklistItem[], id: string, to: number): TaskChecklistItem[] {
  const from = entries.findIndex((entry) => entry.id === id);
  if (from < 0) return entries;

  const next = [...entries];
  const [moved] = next.splice(from, 1);
  if (!moved) return entries;
  next.splice(Math.max(0, Math.min(to, next.length)), 0, moved);

  return next;
}

/** Where a drop lands: the dragged item, immediately before or after the row it
 *  was released over. The target index is read from the list with the dragged
 *  item already removed, which is the frame `withMoved` inserts into. */
function dropped(
  entries: TaskChecklistItem[],
  draggedId: string,
  targetId: string,
  below: boolean,
): TaskChecklistItem[] {
  const without = entries.filter((entry) => entry.id !== draggedId);
  const target = without.findIndex((entry) => entry.id === targetId);
  if (target < 0) return entries;

  return withMoved(entries, draggedId, below ? target + 1 : target);
}

/**
 * One line of the checklist.
 *
 * Split out once the row grew a handle, two arrows and a drop indicator: the
 * map that held it inline was longer than the component around it. Its props
 * are flat and numerous on purpose -- grouping them into `drag` and `actions`
 * objects would read as structure where there is only a list.
 *
 * It owns no request and no order. Every control reports an intent upwards, the
 * same division TaskRow and its siblings keep.
 */
function ChecklistRow({
  item,
  index,
  count,
  editing,
  reorderable,
  dragging,
  dropEdge,
  onToggle,
  onStartRename,
  onCancelRename,
  onRename,
  onRemove,
  onMove,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  item: TaskChecklistItem;
  index: number;
  count: number;
  editing: boolean;
  /** Whether this list can be reordered at all right now -- see `settling`. */
  reorderable: boolean;
  dragging: boolean;
  dropEdge: 'above' | 'below' | null;
  onToggle: () => void;
  onStartRename: () => void;
  onCancelRename: () => void;
  onRename: (draft: string) => void;
  onRemove: () => void;
  /** -1 for up, +1 for down. */
  onMove: (delta: number) => void;
  onDragStart: () => void;
  /** True when the pointer is in the lower half of the row, so the drop goes
   *  after it rather than before. */
  onDragOver: (below: boolean) => void;
  onDrop: (below: boolean) => void;
  onDragEnd: () => void;
}) {
  const rowRef = useRef<HTMLLIElement>(null);
  const pending = isPendingItem(item.id);

  /** Which half of the row the pointer is over. The indicator and the drop have
   *  to agree on this, so both read it from here. */
  const belowMidpoint = (event: { clientY: number }): boolean => {
    const box = rowRef.current?.getBoundingClientRect();
    return box ? event.clientY > box.top + box.height / 2 : false;
  };

  return (
    <li
      ref={rowRef}
      className={`group/item relative flex items-center gap-2 transition-opacity ${
        dragging ? 'opacity-40' : ''
      }`}
      onDragOver={(event) => {
        if (!reorderable) return;
        // Without this the drop never fires: the default for a dragover is to
        // refuse the drop.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        onDragOver(belowMidpoint(event));
      }}
      onDrop={(event) => {
        if (!reorderable) return;
        event.preventDefault();
        onDrop(belowMidpoint(event));
      }}
    >
      {/* The drop line. `pointer-events-none` because it sits over the row it
          belongs to, and an element that hit-tests there would swallow the
          `dragover` that keeps it alive -- it would flicker out from under the
          pointer that was drawing it. */}
      {dropEdge ? (
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute inset-x-0 h-0.5 rounded-full bg-honey-500 ${
            dropEdge === 'above' ? '-top-px' : '-bottom-px'
          }`}
        />
      ) : null}

      {/*
        The drag handle.

        A `<span>`, not a button, and `aria-hidden`: native drag is a pointer
        gesture with no keyboard equivalent, so exposing it to a screen reader
        would offer a control that cannot be operated. The arrows below are the
        accessible half of this feature rather than a fallback for it -- they
        are also the only half that works on a touch screen, where HTML5 drag
        events are never fired at all.

        Visible at rest, unlike every other control on the row. Those are
        actions you go looking for; this is the only thing on screen that says
        the list can be reordered, and an affordance revealed by hover is one
        most people never find.
      */}
      <span
        aria-hidden="true"
        draggable={reorderable && !pending}
        title={reorderable ? 'Drag to reorder' : undefined}
        onDragStart={(event) => {
          // Firefox will not start a drag without data on the transfer, even
          // when nothing reads it back.
          event.dataTransfer.setData('text/plain', item.id);
          event.dataTransfer.effectAllowed = 'move';
          // The handle is what is draggable, so the ghost would be a grip on
          // its own. The row is what is being moved and is what should follow
          // the pointer.
          if (rowRef.current) {
            const box = rowRef.current.getBoundingClientRect();
            event.dataTransfer.setDragImage(
              rowRef.current,
              event.clientX - box.left,
              event.clientY - box.top,
            );
          }
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        className={`shrink-0 text-stone-300 transition ${
          reorderable && !pending
            ? 'cursor-grab active:cursor-grabbing group-hover/item:text-stone-400'
            : 'opacity-40'
        }`}
      >
        <GripIcon className="h-3.5 w-3.5" />
      </span>

      <button
        type="button"
        // `role="checkbox"` on a button rather than a real
        // `<input type="checkbox">`, the same choice TaskRow and
        // FocusTaskRow make: the forms plugin is opt-in here, so a
        // native control would render as unstyled platform chrome.
        role="checkbox"
        aria-checked={item.done}
        // No id to PATCH yet -- the same reason an optimistic task row
        // renders without its controls.
        disabled={pending}
        onClick={onToggle}
        aria-label={`${item.done ? 'Untick' : 'Tick'}: ${item.title}`}
        className={`shrink-0 ${CHECKBOX_BOX} disabled:cursor-default disabled:opacity-50 ${
          item.done
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'border-stone-300 text-transparent hover:border-emerald-500 hover:text-emerald-500'
        }`}
      >
        {/* Emerald and a tick, matching FocusTaskRow's mark-done rather
            than TaskRow's honey selection circle: this one says
            finished, not chosen. */}
        <CheckIcon className="h-2.5 w-2.5" />
      </button>

      {editing ? (
        <ChecklistItemInput title={item.title} onCommit={onRename} onCancel={onCancelRename} />
      ) : (
        <button
          type="button"
          title="Click to rename"
          disabled={pending}
          onClick={onStartRename}
          // Two-stage reveal, the same idea as the row's priority cell:
          // at rest it is just the line of text, hovered it is plainly a
          // control. A permanent box on every item would turn a list you
          // read into a form you fill in.
          className={`-mx-1 min-w-0 flex-1 truncate rounded px-1 py-0.5 text-left text-dense transition hover:bg-stone-200/70 disabled:hover:bg-transparent ${
            item.done ? 'text-stone-500 line-through' : 'text-stone-800'
          }`}
        >
          {item.title}
        </button>
      )}

      {pending ? (
        <span className="shrink-0 text-meta text-stone-500">Saving…</span>
      ) : (
        // One group, so the three controls appear and disappear together
        // rather than the row's trailing edge reflowing as each one decides.
        <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition focus-within:opacity-100 group-hover/item:opacity-100">
          {/*
            Disabled rather than hidden at the ends of the list. Hiding them
            would shift the two controls beside them by half a centimetre on
            the first and last rows, so the delete button would sit under a
            different pixel depending on which row you reached for.
          */}
          <RowButton
            label={`Move up: ${item.title}`}
            title="Move up"
            disabled={!reorderable || index === 0}
            onClick={() => onMove(-1)}
          >
            <ChevronUpIcon className="h-3.5 w-3.5" />
          </RowButton>

          <RowButton
            label={`Move down: ${item.title}`}
            title="Move down"
            disabled={!reorderable || index === count - 1}
            onClick={() => onMove(1)}
          >
            <ChevronIcon className="h-3.5 w-3.5 text-current" />
          </RowButton>

          <RowButton
            label={`Remove ${item.title}`}
            onClick={onRemove}
            className="hover:bg-red-50 hover:text-red-700"
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </RowButton>
        </span>
      )}
    </li>
  );
}

/**
 * One of the row's trailing controls.
 *
 * The reveal lives on the wrapping span rather than here, so these only carry
 * what differs between them. `opacity-0` rather than TaskRow's `invisible`:
 * that one hides a control sitting over a full-bleed row link, where an
 * opacity-0 element would still hit-test above it. There is no such link in
 * this panel section -- but there is now a drop target, and `visibility:
 * hidden` would take these out of the tab order, which is where the keyboard
 * reaches them from.
 */
function RowButton({
  label,
  title,
  disabled = false,
  className = '',
  onClick,
  children,
}: {
  label: string;
  title?: string;
  disabled?: boolean;
  className?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`rounded p-0.5 text-stone-500 transition hover:bg-stone-200/70 hover:text-stone-900 disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-stone-500 ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * The rename field.
 *
 * Split out so it can hold its own draft: keeping that in the parent would
 * re-render every sibling row on each keystroke.
 *
 * Escape calls `preventDefault`, which is not cosmetic -- SidePanel listens for
 * Escape and would otherwise close the whole panel out from under the edit. The
 * panel's own EditableTitle documents the same trap.
 */
function ChecklistItemInput({
  title,
  onCommit,
  onCancel,
}: {
  title: string;
  onCommit: (draft: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(title);

  return (
    <Input
      compact
      autoFocus
      value={draft}
      aria-label="Checklist item"
      className="min-w-0 flex-1"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onCommit(draft);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
      }}
    />
  );
}
